<script setup lang="ts">
import { PrimaryButton } from '@/components/Buttons'
import DateTimePickerField from '@/components/Fields/DateTimePickerField.vue'
import { StackLayout } from '@/components/Layouts'
import FullScreenLoader from '@/components/LoadingSpinner/FullScreenLoader.vue'
import { BaseDialog } from '@/components/Modals/Dialog'
import { formatDate } from '@/utils/dateUtils'
import moment from 'moment'
import type { DateTimePickerProps } from '../../../types/components/DateTimePicker'
import type { RentalDateModel } from '../../../types/models/RentalModel'

type Props = {
    loading: boolean
    label?: string
    title?: string
    valueDate?: RentalDateModel
    startDateProps?: DateTimePickerProps & Record<string, unknown>
    endDateProps?: DateTimePickerProps & Record<string, unknown>

    open?: boolean
}
const props = defineProps<Props>()
type Emits = {
    (e: 'change', data: Partial<RentalDateModel>): void
    (e: 'confirm'): void

    (e: 'open', value: boolean): void
}
const emit = defineEmits<Emits>()

const handleChangeStartDate = (value: Date) =>
    emit('change', {
        start_at: value,
        end_at: moment(value).isAfter(moment(props.valueDate?.end_at).utc())
            ? moment(value).utc().toDate()
            : props.valueDate?.end_at
    })
</script>

<template>
    <FullScreenLoader :isLoading="loading" />
    <PrimaryButton
        :label="label ?? $t('device.rental.action')"
        @click="$emit('open', true)"
        v-bind="$attrs" />
    <BaseDialog
        :title="title ?? $t('device.rental.title')"
        :open="!!open"
        @close="$emit('open', false)"
        @confirm="$emit('confirm')"
        class="dialog-with-dropdown">
        <template #content>
            <StackLayout direction="column" :gap="16" class="full-width">
                <slot />
                <StackLayout direction="row" isResponsive :gap="8" class="full-width">
                    <DateTimePickerField
                        :label="$t('device.rental.startAt')"
                        :modelValue="formatDate(valueDate?.start_at as Date)"
                        @change="handleChangeStartDate(moment($event).utc().toDate())"
                        class="full-width"
                        v-bind="startDateProps" />
                    <DateTimePickerField
                        :label="$t('device.rental.endAt')"
                        :modelValue="formatDate(valueDate?.end_at as Date)"
                        @change="$emit('change', { end_at: moment($event).utc().toDate() })"
                        :minDatetime="moment(valueDate?.start_at).utc().toDate()"
                        class="full-width"
                        v-bind="endDateProps" />
                </StackLayout>
            </StackLayout>
        </template>
    </BaseDialog>
</template>

<style scoped lang="scss">
.fixed-dialog-dropdown {
    &:deep(.dropdown-content) {
        position: fixed;
        display: flex;
        flex-flow: wrap;
    }
}

.dialog-with-dropdown {
    @media (min-width: 1024px) {
        &:deep(.dropdown-content) {
            position: fixed;
            display: flex;
            flex-flow: wrap;
            & .content-horizontal-timepicker {
                border-right: 1px solid $primary;
            }
        }
        & :deep(footer) {
            & .dropdown-content {
                position: relative;
            }
            & select {
                background-color: var(--color-background-secondary);
                color: var(--color-text);
                & option {
                    color: var(--color-text);
                }
            }
        }
    }
    & :deep(footer) {
        & select {
            background-color: var(--color-background-secondary);
            color: var(--color-text);
            & option {
                color: var(--color-text);
            }
        }
    }
}
</style>
